import { z } from 'zod';
import { slugSchema } from './setup/slug';

// ---------------------------------------------------------------------------
// User Context Panel — admin-facing identity + state inspection/mutation.
// ---------------------------------------------------------------------------

// A JSON Schema describing a state's shape. Kept as `record(z.unknown())`
// because the panel renders it as JSON (we don't constrain user-authored
// schemas here).
const jsonSchemaSchema = z.record(z.unknown());

// Identity is sourced from the `user` table (the conversation's end user).
// hub_user-only fields (hub_user_id, enabled, hub_team_id, source) are present
// only when an access-grant row exists (account-mode); optional otherwise.
const hubUserIdentity = z.object({
  user_id: z.string().nullable(),
  user_name: z.string().nullable(),
  user_email: z.string().nullable(),
  user_name_locked: z.boolean(),
  created_at: z.string(),
  updated_at: z.string().nullable(),
  hub_user_id: z.string().optional(),
  enabled: z.boolean().optional(),
  hub_team_id: z.string().nullable().optional(),
  source: z.enum(['grant', 'auto']).optional(),
});

const stateValueEntry = z.object({
  state_slug: z.string(),
  state_name: z.string(),
  state_description: z.string().nullable().optional(),
  scope: z.enum(['user', 'conversation']),
  json_schema: jsonSchemaSchema,
  initial_value: z.unknown(),
  value: z.unknown(),
  is_reserved: z.boolean(),
});

export const getHubUserContextResponse = z.object({
  user: hubUserIdentity,
  user_states: z.array(stateValueEntry),
  conversation_states: z.array(stateValueEntry),
});

export type GetHubUserContextResponse = z.infer<typeof getHubUserContextResponse>;
export type HubUserIdentity = z.infer<typeof hubUserIdentity>;
export type StateValueEntry = z.infer<typeof stateValueEntry>;

export const updateHubUserBody = z.object({
  // Non-empty, non-null. `.nullable()` here would bypass `.min(1)` and let an
  // admin arm the lock on a null name — see hub-do.ts updateHubUser null-guard.
  user_name: z.string().min(1).max(120),
});

export const updateHubUserResponse = z.object({
  user: hubUserIdentity,
});

export type UpdateHubUserBody = z.infer<typeof updateHubUserBody>;
export type UpdateHubUserResponse = z.infer<typeof updateHubUserResponse>;

export const unlockHubUserNameResponse = z.object({
  user: hubUserIdentity,
});

export type UnlockHubUserNameResponse = z.infer<typeof unlockHubUserNameResponse>;

export const stateResetParams = z.object({
  stateSlug: slugSchema,
});

export const stateResetResponse = z.object({
  ok: z.literal(true),
});

export type StateResetParams = z.infer<typeof stateResetParams>;
export type StateResetResponse = z.infer<typeof stateResetResponse>;
